// Result — sum of Ok<A> and Err<E>. Tagged shape with prefixed tags.
//
// Consumer API: `Result.ok(x)`, `Result.err(e)`, `Result.isOk(r)`,
// `Result.isErr(r)`, `Result.match({ok, err})(r)`, `Result.map(f)(r)`,
// `Result.and(f)(r)` (flatMap), `Result.combineA([...])`, etc.
//
// `_tag` is the internal discriminant — never read it from consumer
// code; use `isOk` / `isErr` for guards and `match` for dispatch.
export { and, combineA, combineO, err, isErr, isOk, map, mapErr, match, ok, or, tee, tryCatch, unwrapOr, };
const ok = (value) => ({ _tag: "Result.Ok", value });
const err = (error) => ({ _tag: "Result.Err", error });
const isOk = (r) => r._tag === "Result.Ok";
const isErr = (r) => r._tag === "Result.Err";
const map = (f) => (r) => isOk(r) ? ok(f(r.value)) : r;
const mapErr = (f) => (r) => isErr(r) ? err(f(r.error)) : r;
// and — the flatMap / bind / chain operation. Sequences a second
// Result-returning step onto an existing Result. Short-circuits on
// error.
const and = (f) => (r) => isOk(r) ? f(r.value) : r;
// or — dual of `and`. Recover from an error by producing a new
// Result. Skipped when the input is already Ok.
const or = (f) => (r) => isErr(r) ? f(r.error) : r;
// tee — side-effect on the Result without changing it. Handy for
// logging inside a pipe chain.
const tee = (f) => (r) => {
    f(r);
    return r;
};
// match — reduce a Result to a value by handling both variants.
// Consumer-facing dispatch; no _tag grep needed downstream.
const match = (cases) => (r) => isOk(r) ? cases.ok(r.value) : cases.err(r.error);
// combineA — traverse an array of Results. Returns Ok of the value
// array when every input is Ok; otherwise Err of the error array.
// Accumulates all errors (does NOT short-circuit).
const combineA = (rs) => {
    const values = [];
    const errors = [];
    for (const r of rs) {
        if (isOk(r))
            values.push(r.value);
        else
            errors.push(r.error);
    }
    return errors.length > 0 ? err(errors) : ok(values);
};
// combineO — same shape for an object of Results.
const combineO = (o) => {
    const record = {};
    const errors = [];
    for (const [k, v] of Object.entries(o)) {
        if (isOk(v))
            record[k] = v.value;
        else
            errors.push(v.error);
    }
    return errors.length > 0 ? err(errors) : ok(record);
};
// tryCatch — turn a throwing thunk into a Result. Boundary
// converter; not for use inside pure core.
const tryCatch = (f, onErr) => {
    try {
        return ok(f());
    }
    catch (e) {
        return err(onErr(e));
    }
};
// unwrapOr — read the value with a fallback for the Err case.
const unwrapOr = (fallback) => (r) => isOk(r) ? r.value : fallback;
//# sourceMappingURL=result.js.map